/**
 * Maps an executed `capabilities` tool result to the domain query invalidations
 * it implies, so a UI reflecting the same data as an assistant write
 * refetches without a reload.
 *
 * Keys off `capabilityId` in the tool *output*, not `toolName` and not the
 * requested action. `capabilities` is the single stable tool name for every
 * page, so `toolName` carries no information about which capability ran.
 * `serializeResult` (`apps/api/src/assistant/capabilities-tool.ts`) puts
 * `capabilityId` on every resolved `call` branch — success, execution
 * failure, flag-disabled, planned — while the `list` branch (and the
 * early-exit `call` failures: missing/unknown capabilityId, invalid args)
 * never carries one. That excludes `list` discovery, but NOT read-only
 * `call`s: `read_map` and `get_capability` carry a `capabilityId` exactly
 * like `create_area` does.
 *
 * So presence alone is not enough, and the difference is not cosmetic — the
 * product-mapping doctrine tells the assistant to read the complete map
 * before proposing anything, so a prefix-only rule would refetch the map on
 * every single turn. Only the capabilities that actually write are listed
 * below.
 *
 * Unknown ids invalidate nothing, so the failure mode of a stale list is a
 * missing live refresh (the behaviour before this existed) rather than a
 * wrong render — and `capabilityInvalidation.test.ts` pins the read-only ones.
 *
 * @module features/assistant/capabilityInvalidation
 */

/**
 * Capability ids that mutate server state. Mirrors the registries in
 * `apps/api/src/assistant/capabilities/{product-map,registry}.ts`; a new
 * writing capability must be added here to refresh the page live.
 */
const MUTATING_CAPABILITY_IDS: ReadonlySet<string> = new Set([
  // productMap.* — reads (read_map, get_capability, lookup_code,
  // get_coverage) are deliberately absent.
  'productMap.create_area',
  'productMap.update_area',
  'productMap.deprecate_area',
  'productMap.reorder_areas',
  'productMap.create_capability',
  'productMap.update_capability',
  'productMap.deprecate_capability',
  'productMap.move_capability',
  'productMap.set_capability_flows',
  'productMap.accept_proposal',
  // flow.* — graph edits plus the two writes that also touch the
  // product-map keys (see FLOW_CAPABILITIES_ALSO_TOUCHING_PRODUCT_MAP below).
  'flow.create_node',
  'flow.update_node',
  'flow.delete_node',
  'flow.move_node',
  'flow.create_edge',
  'flow.delete_edge',
  'flow.set_product_capabilities',
  'flow.propose_changes',
  // exploration.start — the Explore kickoff onboarding's finish capability;
  // it writes the property's exploration schedule and fires its first cycle.
  'exploration.start',
]);

/**
 * `flow.*` capabilities that also write data the Flow Designer reads under
 * the product-map query keys (`useWorkflowCapabilities`,
 * `['product-map', 'workflow-capabilities', workflowId]`), not just
 * `['workflow-detail', workflowId]`:
 * - `set_product_capabilities` writes `capabilityWorkflowLinks` directly.
 * - `propose_changes` persists a change request the designer renders through
 *   the same product-map-backed view.
 */
const FLOW_CAPABILITIES_ALSO_TOUCHING_PRODUCT_MAP: ReadonlySet<string> = new Set([
  'flow.set_product_capabilities',
  'flow.propose_changes',
]);

/** Mirrors `ResolvedPageContext['params']` without importing the whole module. */
export type CapabilityInvalidationParams = Record<string, string | undefined>;

export const ASSISTANT_ACTIVITIES_QUERY_KEY = ['assistant', 'activities'] as const;

export type InvalidationTarget =
  | { kind: 'product-map' }
  | { kind: 'workflow'; workflowId: string }
  | { kind: 'assistant-activities'; queryKey: typeof ASSISTANT_ACTIVITIES_QUERY_KEY }
  | { kind: 'exploration' };

function readCapabilityId(toolOutput: unknown): string | null {
  if (typeof toolOutput !== 'object' || toolOutput === null) return null;
  const capabilityId = (toolOutput as { capabilityId?: unknown }).capabilityId;
  return typeof capabilityId === 'string' ? capabilityId : null;
}

/**
 * A flow capability callable from any page echoes the flow it acted on via
 * `result.workflowId`, so that isn't necessarily the page's
 * `params.workflowId`. Prefer the echoed result value over the page param.
 */
function readResultWorkflowId(toolOutput: unknown): string | null {
  if (typeof toolOutput !== 'object' || toolOutput === null) return null;
  const result = (toolOutput as { result?: unknown }).result;
  if (typeof result !== 'object' || result === null) return null;
  const workflowId = (result as { workflowId?: unknown }).workflowId;
  return typeof workflowId === 'string' ? workflowId : null;
}

function isSuccessfulMaintenanceStart(toolOutput: unknown): boolean {
  if (readCapabilityId(toolOutput) !== 'flow.run_maintenance') return false;
  if (typeof toolOutput !== 'object' || toolOutput === null) return false;
  const output = toolOutput as { ok?: unknown; result?: unknown };
  if (output.ok !== true || typeof output.result !== 'object' || output.result === null)
    return false;
  const result = output.result as { success?: unknown; activityId?: unknown };
  return result.success === true && typeof result.activityId === 'string';
}

/**
 * Resolve the invalidations implied by one `capabilities` tool result.
 *
 * Returns a discriminated union rather than raw query keys: the
 * `product-map` case is invalidated through the existing
 * `useInvalidateProductMap` (which owns the module-private `productMapKeys`
 * object), not by this module re-deriving the same key literal.
 */
export function resolveCapabilityInvalidation(
  toolOutput: unknown,
  params: CapabilityInvalidationParams
): InvalidationTarget[] {
  const capabilityId = readCapabilityId(toolOutput);
  if (!capabilityId) return [];
  if (isSuccessfulMaintenanceStart(toolOutput)) {
    return [{ kind: 'assistant-activities', queryKey: ASSISTANT_ACTIVITIES_QUERY_KEY }];
  }
  if (!MUTATING_CAPABILITY_IDS.has(capabilityId)) return [];

  if (capabilityId === 'exploration.start') {
    return [{ kind: 'exploration' }];
  }
  if (capabilityId.startsWith('productMap.')) {
    return [{ kind: 'product-map' }];
  }
  if (capabilityId.startsWith('flow.')) {
    const workflowId = readResultWorkflowId(toolOutput) ?? params.workflowId;
    if (!workflowId) return [];
    const targets: InvalidationTarget[] = [{ kind: 'workflow', workflowId }];
    if (FLOW_CAPABILITIES_ALSO_TOUCHING_PRODUCT_MAP.has(capabilityId)) {
      targets.push({ kind: 'product-map' });
    }
    return targets;
  }
  return [];
}
